import { useState, useEffect, useRef } from 'react';

// Regex patterns for extracting files and dependencies - more flexible matching
// artifactRegex removed as we parse actions globally for robustness
const actionRegex = /<boltAction\s+type="file"\s+filePath="([^"]+)"\s*>([\s\S]*?)<\/boltAction>/gi;
const depRegex = /<boltAction\s+type="dependency"\s+package="([^"]+)"(?:\s+version="([^"]+)")?[^>]*>(?:<\/boltAction>)?/gi;
const shellActionRegex = /<boltAction\s+type="shell"\s*>([\s\S]*?)<\/boltAction>/gi;
const markdownRegex = /```([a-zA-Z0-9_/.-]+\.(?:tsx|ts|jsx|js|css|html))\s*\n([\s\S]*?)```/g;
const looseCodeRegex = /```(tsx|css|javascript|typescript)\n([\s\S]*?)```/g;

function normalizePath(rawPath: string): string {
  let path = rawPath.trim().replace(/^\/+/, "");

  if (path === "App.tsx" || path.endsWith("/App.tsx") || path === "src/App.tsx") {
    return "/App.tsx";
  }

  if (path === "index.css" || path.endsWith("/index.css") || path === "src/index.css" || path === "styles.css") {
    return "/styles.css";
  }

  return "/" + path;
}

interface ExtractedContent {
  files: Record<string, string>;
  dependencies: Record<string, string>;
  shellActions: string[];
  deletedFiles?: string[];
}

const deleteActionRegex = /<boltAction\s+type="delete"\s+filePath="([^"]+)"\s*\/?>/gi;
const deleteActionRegex2 = /<boltAction\s+filePath="([^"]+)"\s+type="delete"\s*\/?>/gi;

function extractFromMessages(messages: { role: string; content: string }[]): ExtractedContent {
  const files: Record<string, string> = {};
  const dependencies: Record<string, string> = {};
  const shellActions: string[] = [];
  const deletedFiles: Set<string> = new Set();

  for (const message of messages) {
    if (message.role !== "assistant") continue;
    const content = message.content ?? '';  // SAFETY: Ensure content is always a string

    // Reset regex lastIndex
    markdownRegex.lastIndex = 0;
    looseCodeRegex.lastIndex = 0;

    // 1. XML parsing (Global search, ignoring artifact wrapper for robustness)
    let foundBoltActions = false;

    // Regex patterns for file actions matching BoltArtifactRenderer
    const actionRegex1 = /<boltAction\s+type="file"\s+filePath="([^"]+)"\s*>([\s\S]*?)<\/boltAction>/gi;
    const actionRegex2 = /<boltAction\s+filePath="([^"]+)"\s+type="file"\s*>([\s\S]*?)<\/boltAction>/gi;

    // Extract files - Pattern 1 (type="file" filePath="...")
    actionRegex1.lastIndex = 0;
    let actionMatch;
    while ((actionMatch = actionRegex1.exec(content)) !== null) {
      foundBoltActions = true;
      const [, filePath, fileContent] = actionMatch;
      const normalized = normalizePath(filePath);
      files[normalized] = String(fileContent ?? "").trim();
      deletedFiles.delete(normalized); // If recreated, remove from deleted list
    }

    // Extract files - Pattern 2 (filePath="..." type="file")
    actionRegex2.lastIndex = 0;
    while ((actionMatch = actionRegex2.exec(content)) !== null) {
      foundBoltActions = true;
      const [, filePath, fileContent] = actionMatch;
      const normalized = normalizePath(filePath);
      // Don't overwrite if found by pattern 1 (priority to standard format)
      if (!files[normalized]) {
        files[normalized] = String(fileContent ?? "").trim();
        deletedFiles.delete(normalized);
      }
    }

    // Extract DELETE actions
    deleteActionRegex.lastIndex = 0;
    let deleteMatch;
    while ((deleteMatch = deleteActionRegex.exec(content)) !== null) {
      foundBoltActions = true;
      const [, filePath] = deleteMatch;
      const normalized = normalizePath(filePath);
      delete files[normalized]; // Remove from files map
      deletedFiles.add(normalized); // Add to deleted list
    }

    deleteActionRegex2.lastIndex = 0;
    while ((deleteMatch = deleteActionRegex2.exec(content)) !== null) {
      foundBoltActions = true;
      const [, filePath] = deleteMatch;
      const normalized = normalizePath(filePath);
      delete files[normalized];
      deletedFiles.add(normalized);
    }

    // Extract dependencies
    depRegex.lastIndex = 0;
    let depMatch;
    while ((depMatch = depRegex.exec(content)) !== null) {
      foundBoltActions = true;
      const [, name, version] = depMatch;
      dependencies[name] = version || "latest";
    }

    // Extract shell actions
    shellActionRegex.lastIndex = 0;
    let shellMatch;
    while ((shellMatch = shellActionRegex.exec(content)) !== null) {
      foundBoltActions = true;
      const command = String(shellMatch[1] ?? "").trim();

      // Track npm/npx commands
      if (command.startsWith('npm install') || command.startsWith('npm i') || command.startsWith('npx ')) {
        if (!shellActions.includes(command)) {
          shellActions.push(command);
        }
      }

      // Track rm commands (Retroactive fix for legacy deletion)
      if (command.startsWith('rm ')) {
        // Parse rm command carefully, stopping at shell operators (&& || ; |)
        const beforeOperator = command.split(/\s*(?:&&|\|\||\||;)\s*/)[0]; // Only take first part before operators
        const parts = beforeOperator.split(' ');
        // Filter out flags (-rf, -f, -r), "rm", and common shell commands/operators
        const shellCommands = new Set(['rm', 'npm', 'npx', 'cd', 'ls', 'cat', 'echo', 'install', 'run', 'dev', 'build', 'start', '&&', '||', ';']);
        const potentialFiles = parts.filter(p =>
          !p.startsWith('-') &&
          !shellCommands.has(p) &&
          p.length > 0 &&
          (p.includes('/') || p.includes('.') || p === 'node_modules' || p === 'package-lock.json') // Must look like a file/dir path
        );

        for (const rawPath of potentialFiles) {
          const normalized = normalizePath(rawPath);
          // Verify it looks like a file path (not empty)
          if (normalized && normalized !== '/') {
            delete files[normalized];
            deletedFiles.add(normalized);
          }
        }
      }
    }

    // 2. Markdown fallback (Only if no Bolt actions found in this message)
    if (!foundBoltActions) {
      let mdMatch;
      while ((mdMatch = markdownRegex.exec(content)) !== null) {
        const [, filename, code] = mdMatch;
        files[normalizePath(filename)] = String(code ?? "").trim();
      }

      // 3. Loose code blocks
      if (Object.keys(files).length === 0) {
        let looseMatch;
        while ((looseMatch = looseCodeRegex.exec(content)) !== null) {
          const [, lang, code] = looseMatch;
          const safeCode = String(code ?? "").trim();
          if (safeCode.includes("export default function") || lang === "tsx") {
            files["/App.tsx"] = safeCode;
          } else if (safeCode.includes("@tailwind") || lang === "css") {
            files["/styles.css"] = safeCode;
          }
        }
      }
    }
  }

  return { files, dependencies, shellActions, deletedFiles: Array.from(deletedFiles) };
}

export interface ProjectFilesContent {
  files: Record<string, string>;
  dependencies: Record<string, string>;
  shellActions: string[];
  deletedFiles?: string[];
}

/**
 * Hook that extracts project files from AI messages with debouncing during streaming
 * Uses longer debounce during streaming for better performance
 */
export function useExtractedProjectFiles(
  messages: { role: string; content: string }[],
  isLoading: boolean,
  debounceMs: number = 2000 // Increased for better performance during streaming
): ProjectFilesContent {
  const [content, setContent] = useState<ProjectFilesContent>({ files: {}, dependencies: {}, shellActions: [], deletedFiles: [] });
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastContentHashRef = useRef<string>("");

  useEffect(() => {
    // Create a simple hash of the content to detect real changes
    const contentHash = messages
      .filter(m => m.role === "assistant")
      .map(m => (m.content ?? '').length)  // SAFETY: Handle undefined content
      .join("-");

    // Skip if content hasn't changed
    if (contentHash === lastContentHashRef.current) {
      return;
    }

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // If not loading, update immediately
    if (!isLoading) {
      lastContentHashRef.current = contentHash;
      const extracted = extractFromMessages(messages);
      if (Object.keys(extracted.files).length > 0) {
        console.log("[ProjectFiles] Files extracted:", Object.keys(extracted.files));
        console.log("[ProjectFiles] Dependencies:", Object.keys(extracted.dependencies));
        setContent(extracted);
      }
      return;
    }

    // During loading, use longer debounce to reduce Sandpack re-renders
    // This prevents the bundler from running too frequently
    timeoutRef.current = setTimeout(() => {
      lastContentHashRef.current = contentHash;
      const extracted = extractFromMessages(messages);
      if (Object.keys(extracted.files).length > 0) {
        console.log("[ProjectFiles] Files extracted (debounced):", Object.keys(extracted.files));
        console.log("[ProjectFiles] Dependencies (debounced):", Object.keys(extracted.dependencies));
        setContent(extracted);
      }
    }, debounceMs);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [messages, isLoading, debounceMs]);

  return content;
}
